/* ==========================================================================
   c-mega-menu
   Behaviour for sections/c-mega-menu.liquid.

   Desktop: hover opens a dropdown, with a short grace period on leave so the
   pointer can travel from the label to the panel. Click also toggles, which is
   what keyboard and touch users get.
   Mobile: hamburger opens the drawer; each nav item is an accordion that
   expands in place (Figma uses +/- rows, not sliding sub-panels).

   Sticky is left to CSS (position: sticky) — no scroll listener needed.
   ========================================================================== */

(function () {
  if (customElements.get('c-mega-menu')) return;

  var CLOSE_DELAY = 120;

  class CMegaMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-cmm-item]'));
      this.drawer = this.querySelector('[data-cmm-drawer]');
      this.overlay = this.querySelector('[data-cmm-overlay]');
      this.closeTimer = null;
      this.abort = new AbortController();

      this.searchPanel = this.querySelector('[data-cmm-search-panel]');
      this.searchToggle = this.querySelector('[data-cmm-search-toggle]');

      this.bindDesktop();
      this.bindDrawer();
      this.bindAccordion();
      this.bindSearch();
      this.bindGlobal();
      this.watchWishlistCount();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.countWaiter) this.countWaiter.disconnect();
      if (this.countObserver) this.countObserver.disconnect();
      clearTimeout(this.closeTimer);
      this.unlockScroll();
    }

    get signal() {
      return this.abort.signal;
    }

    /* ---------------------------------------------------------------- desktop */

    bindDesktop() {
      this.items.forEach((item) => {
        var trigger = item.querySelector('[data-cmm-trigger]');
        if (!trigger || !item.querySelector('[data-cmm-panel]')) return;

        item.addEventListener('mouseenter', () => this.open(item), { signal: this.signal });
        item.addEventListener('mouseleave', () => this.scheduleClose(item), { signal: this.signal });

        trigger.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            item.classList.contains('is-open') ? this.close(item) : this.open(item);
          },
          { signal: this.signal }
        );

        // Tabbing out of the whole item closes it, so keyboard users don't
        // leave an orphaned panel open behind them.
        item.addEventListener(
          'focusout',
          (e) => {
            if (!item.contains(e.relatedTarget)) this.close(item);
          },
          { signal: this.signal }
        );
      });
    }

    open(item) {
      clearTimeout(this.closeTimer);
      this.items.forEach((other) => {
        if (other !== item) this.close(other);
      });
      item.classList.add('is-open');
      var trigger = item.querySelector('[data-cmm-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    close(item) {
      item.classList.remove('is-open');
      var trigger = item.querySelector('[data-cmm-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }

    scheduleClose(item) {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.close(item), CLOSE_DELAY);
    }

    /* ----------------------------------------------------------------- drawer */

    bindDrawer() {
      var openBtn = this.querySelector('[data-cmm-drawer-open]');
      var closeBtn = this.querySelector('[data-cmm-drawer-close]');

      if (openBtn) {
        openBtn.addEventListener('click', () => this.openDrawer(), { signal: this.signal });
      }
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });
      }
    }

    openDrawer() {
      if (!this.drawer) return;
      this.placeSearch();
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.lockScroll();

      // Remember where focus came from so closing can hand it back, instead of
      // dropping the user at the top of the document.
      this.drawerOpener = document.activeElement;

      var first = this.drawer.querySelector('[data-cmm-drawer-close]');
      if (first) first.focus();
    }

    closeDrawer() {
      if (!this.drawer) return;
      var wasOpen = this.drawer.classList.contains('is-open');
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.unlockScroll();

      if (wasOpen && this.drawerOpener && document.contains(this.drawerOpener)) {
        this.drawerOpener.focus();
      }
      this.drawerOpener = null;
    }

    /* The drawer covers the page, so Tab must not walk into the content behind
       it. Cycles focus between the first and last control instead. */
    trapFocus(e) {
      if (e.key !== 'Tab') return;
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;

      var focusables = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, summary, [tabindex]:not([tabindex="-1"])'
      );
      var visible = Array.prototype.filter.call(focusables, function (n) {
        return n.offsetParent !== null;
      });
      if (!visible.length) return;

      var first = visible[0];
      var last = visible[visible.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      this.prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this.prevOverflow !== undefined) {
        document.body.style.overflow = this.prevOverflow;
        this.prevOverflow = undefined;
      }
    }

    /* -------------------------------------------------------------- accordion */

    bindAccordion() {
      var triggers = this.querySelectorAll('[data-cmm-acc-trigger]');
      triggers.forEach((trigger) => {
        trigger.addEventListener(
          'click',
          () => {
            var row = trigger.closest('[data-cmm-acc-item]');
            if (!row) return;
            var isOpen = row.classList.toggle('is-open');
            trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          },
          { signal: this.signal }
        );
      });
    }

    /* ----------------------------------------------------------------- search */

    bindSearch() {
      this.placeSearch();

      // Re-home the field when the layout flips, not on every resize tick.
      this.searchQuery = window.matchMedia('(max-width: 977px)');
      this.searchQuery.addEventListener('change', () => this.placeSearch(), { signal: this.signal });

      if (!this.searchPanel || !this.searchToggle) return;

      this.searchToggle.addEventListener(
        'click',
        () => {
          this.searchPanel.classList.contains('is-open') ? this.closeSearch() : this.openSearch();
        },
        { signal: this.signal }
      );
    }

    /* Moves the single <predictive-search> between the desktop overlay and the
       drawer. One element, because Dawn's script locates its results container
       by the id #predictive-search — a second copy would duplicate that id and
       the script gives no way to point at another name.

       Moving is safe here: the active PredictiveSearch class binds its input
       listener in the constructor, which runs once when the element is first
       upgraded, so relocating the same node keeps it wired. */
    placeSearch() {
      var field = this.querySelector('predictive-search');
      var slot = this.querySelector('[data-cmm-search-slot]');
      var home = this.querySelector('[data-cmm-search-home]');
      if (!field || !slot || !home) return;

      var target = window.matchMedia('(max-width: 977px)').matches ? slot : home;
      if (field.parentElement === target) return;

      // Keep it as the overlay's first child so the suggested-products grid
      // that follows it stays below the field.
      target.insertBefore(field, target.firstChild);
    }

    openSearch() {
      if (!this.searchPanel) return;
      // Re-home before showing: the matchMedia listener is the fast path, but
      // it can miss (some environments don't fire `change` on a programmatic
      // resize), and an empty overlay is a worse failure than a redundant check.
      this.placeSearch();
      this.closeAll();
      this.searchPanel.classList.add('is-open');
      this.searchToggle.setAttribute('aria-expanded', 'true');
      var field = this.searchPanel.querySelector('input[type="search"]');
      if (field) field.focus();
    }

    closeSearch() {
      if (!this.searchPanel || !this.searchPanel.classList.contains('is-open')) return;
      this.searchPanel.classList.remove('is-open');
      this.searchToggle.setAttribute('aria-expanded', 'false');
    }

    /* --------------------------------------------------------------- wishlist */

    /* The Swym app injects its wishlist trigger and count after we mount, then
       keeps updating the number as items are saved. An empty wishlist reads
       "0", and the header design shows a bare heart in that state, so the
       count pill is flagged empty rather than sitting there as a permanent
       lilac dot. Observed rather than read once because both the injection
       and the updates happen on the app's own schedule. */
    watchWishlistCount() {
      var sync = () => {
        this.swapWishlistGlyph();
        var bubble = this.querySelector('.swym-header-icon-count-bubble');
        if (!bubble) return false;
        var value = (bubble.textContent || '').trim();
        bubble.classList.toggle('is-empty', value === '' || value === '0');
        return true;
      };

      if (!sync()) {
        // Not injected yet — wait for it, then keep watching the value.
        var waiting = new MutationObserver(() => {
          if (sync()) {
            waiting.disconnect();
            this.observeCount();
          }
        });
        waiting.observe(this, { childList: true, subtree: true });
        this.countWaiter = waiting;
      } else {
        this.observeCount();
      }
    }

    /* Swym renders a 24px Phosphor heart, which sits heavier than the rest of
       the row. Replaces just the glyph with the Figma one carried in the
       template, leaving the anchor, its click handler and the count pill
       untouched.

       Idempotent by inspecting the glyph rather than by a one-shot flag: the
       app re-renders its icon after we first swap, so a flag left Swym's
       artwork back in place. Re-running is free once ours is in — the viewBox
       check short-circuits, including on the mutation our own swap fires. */
    swapWishlistGlyph() {
      var tpl = this.querySelector('template[data-cmm-heart]');
      var host = this.querySelector('.swym-advanced-header-icon .swym-header-icon-html');
      if (!tpl || !host) return;

      var glyph = tpl.content.querySelector('svg');
      if (!glyph) return;

      var wanted = glyph.getAttribute('viewBox');
      var current = host.querySelector('svg');
      if (!current || current.getAttribute('viewBox') === wanted) return;

      current.replaceWith(glyph.cloneNode(true));
    }

    /* Watches the whole trigger, not just the count node: Swym re-renders the
       anchor's contents on its own schedule, which both resets the count and
       restores its heart. One observer keeps both in the state we want. */
    observeCount() {
      var anchor = this.querySelector('.swym-advanced-header-icon');
      if (!anchor) return;

      this.countObserver = new MutationObserver(() => {
        this.swapWishlistGlyph();
        var bubble = this.querySelector('.swym-header-icon-count-bubble');
        if (!bubble) return;
        var value = (bubble.textContent || '').trim();
        bubble.classList.toggle('is-empty', value === '' || value === '0');
      });
      this.countObserver.observe(anchor, { childList: true, characterData: true, subtree: true });
    }

    /* ----------------------------------------------------------------- global */

    bindGlobal() {
      document.addEventListener(
        'keydown',
        (e) => {
          this.trapFocus(e);
          if (e.key !== 'Escape') return;
          this.closeAll();
          this.closeDrawer();
          this.closeSearch();
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'click',
        (e) => {
          if (this.contains(e.target)) {
            // A click inside the header still closes search when it lands
            // outside the panel — e.g. on a nav label or another icon.
            if (
              this.searchPanel &&
              !this.searchPanel.contains(e.target) &&
              !this.searchToggle.contains(e.target)
            ) {
              this.closeSearch();
            }
            return;
          }
          this.closeAll();
          this.closeSearch();
        },
        { signal: this.signal }
      );
    }
  }

  customElements.define('c-mega-menu', CMegaMenu);
})();
